import { useDeferredValue, useId, useMemo, useState, type Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Copy, Loader2, RefreshCw } from 'lucide-react';
import {
  isMultifocalLensType,
  ProductStatus,
  ProductType,
  type OpticalLabSummary,
  type PrescriptionLensDetails,
  type ProductVariantSummary,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import { useProductQuery, useProductsQuery } from '@/modules/catalog/api/catalog.api';
import type { EyeSide } from '@/modules/medical/examinationForm';
import { useCandidateCheckQuery } from '../api/opticalOrders.api';
import {
  fittingProblem,
  fittingRequired,
  sameLensActive,
  sharedLensStockProblem,
  type BuilderAction,
  type PairDraft,
  type PairUsage,
  type SelectedLens,
} from '../builder/draft';
import { lensRange } from '../builder/format';
import type { BuilderProblem } from '../builder/refusals';
import { Availability } from '../components/Availability';

export type PairVerdict = 'incomplete' | 'checking' | 'ok' | 'problems' | 'unavailable';

interface LensesStepProps {
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
  labs: OpticalLabSummary[];
  verdict: PairVerdict;
}

const EYE_ABBREVIATION: Record<EyeSide, 'OD' | 'OS'> = { right: 'OD', left: 'OS' };

/**
 * Step 3: a lens for each eye, and the fitting heights their designs need.
 *
 * By default one lens is chosen for both eyes, and the order still carries one lens line per eye. Switched off, the right eye comes first and the left second, one above the other in every language, each chosen on its own. A lens is never swapped behind the user's back: two different lenses are merged only after the user confirms. Whether a lens can be made to the prescription is the server's answer, shown against the eye it concerns.
 */
export function LensesStep({
  pair,
  dispatch,
  problems,
  showErrors,
  labs,
  verdict,
}: LensesStepProps) {
  const { t } = useTranslation();
  const switchId = useId();
  const [confirmMerge, setConfirmMerge] = useState(false);
  const labProblem = problems.find((problem) => problem.field === 'lab');
  const shared = sameLensActive(pair);
  const { right, left } = pair.lenses;
  const differing = Boolean(right && left && right.variantId !== left.variantId);

  const toggle = (on: boolean) => {
    if (on && differing) {
      setConfirmMerge(true);
      return;
    }
    dispatch({ type: 'setSameLens', pairId: pair.id, on });
  };

  return (
    <div className="space-y-5">
      <label
        htmlFor={switchId}
        className="border-line flex cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-3"
      >
        <input
          id={switchId}
          type="checkbox"
          role="switch"
          aria-checked={shared}
          checked={shared}
          onChange={(event) => toggle(event.target.checked)}
          className="size-4 shrink-0"
        />
        <span className="min-w-0">
          <span className="text-ink block text-sm font-medium">
            {t('opticalOrders.lens.sameForBoth')}
          </span>
          <span className="text-muted block text-xs">
            {shared
              ? t('opticalOrders.lens.sameForBothOn')
              : t('opticalOrders.lens.sameForBothOff')}
          </span>
        </span>
      </label>

      {shared ? (
        <SharedLensSection
          pair={pair}
          dispatch={dispatch}
          problems={problems}
          showErrors={showErrors}
        />
      ) : (
        (['right', 'left'] as const).map((eye) => (
          <EyeLensSection
            key={eye}
            eye={eye}
            pair={pair}
            dispatch={dispatch}
            problems={problems}
            showErrors={showErrors}
          />
        ))
      )}

      <ConfirmDialog
        isOpen={confirmMerge}
        title={t('opticalOrders.lens.mergeTitle')}
        message={t('opticalOrders.lens.mergeMessage')}
        confirmLabel={t('opticalOrders.lens.mergeConfirm')}
        onCancel={() => setConfirmMerge(false)}
        onConfirm={() => {
          setConfirmMerge(false);
          dispatch({ type: 'copyRightLensToLeft', pairId: pair.id });
          dispatch({ type: 'setSameLens', pairId: pair.id, on: true });
        }}
      />

      <FormField
        label={t('opticalOrders.lab.label')}
        hint={labs.length > 0 ? t('opticalOrders.lab.hint') : t('opticalOrders.lab.none')}
        error={labProblem ? t(labProblem.message.key, labProblem.message.values) : undefined}
      >
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            hasError={Boolean(labProblem)}
            value={pair.labId}
            options={[
              { value: '', label: t('opticalOrders.lab.later') },
              ...labs
                .filter((lab) => lab.isActive)
                .map((lab) => ({ value: lab.id, label: lab.name })),
            ]}
            onChange={(event) =>
              dispatch({ type: 'setLab', pairId: pair.id, labId: event.target.value })
            }
          />
        )}
      </FormField>

      <VerdictLine verdict={verdict} />
    </div>
  );
}

function VerdictLine({ verdict }: { verdict: PairVerdict }) {
  const { t } = useTranslation();
  if (verdict === 'incomplete') return null;
  if (verdict === 'checking') {
    return (
      <p className="text-muted flex items-center gap-2 text-sm" role="status">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('opticalOrders.lens.checking')}
      </p>
    );
  }
  if (verdict === 'ok') {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-700" role="status">
        <CheckCircle2 className="size-4" aria-hidden />
        {t('opticalOrders.lens.checked')}
      </p>
    );
  }
  if (verdict === 'unavailable') {
    return (
      <p className="text-sm text-amber-800" role="status">
        {t('opticalOrders.lens.checkUnavailable')}
      </p>
    );
  }
  return (
    <p className="text-sm text-red-700" role="status">
      {t('opticalOrders.lens.checkProblems')}
    </p>
  );
}

function EyeLensSection({
  eye,
  pair,
  dispatch,
  problems,
  showErrors,
}: {
  eye: EyeSide;
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const lens = pair.lenses[eye];
  const right = pair.lenses.right;
  const abbreviation = EYE_ABBREVIATION[eye];
  const lensProblems = problems.filter((problem) => problem.field === `lens.${eye}`);

  const setLens = (next: SelectedLens | null) =>
    dispatch({ type: 'setLens', pairId: pair.id, eye, lens: next });

  return (
    <section
      aria-labelledby={headingId}
      className="border-line space-y-4 rounded-xl border bg-white p-4"
    >
      <h3 id={headingId} className="text-ink flex items-center gap-2 text-sm font-semibold">
        <abbr
          dir="ltr"
          title={t(`medical.eyes.${abbreviation}`)}
          className="font-mono no-underline"
        >
          {abbreviation}
        </abbr>
        <span aria-hidden>·</span>
        <span>{t(`medical.eyes.${abbreviation}`)}</span>
      </h3>

      {lens ? (
        <div className="space-y-3">
          <SelectedLensSummary lens={lens} />
          <Button variant="secondary" size="sm" onClick={() => setLens(null)}>
            <RefreshCw className="size-4" aria-hidden />
            {t('opticalOrders.lens.change', { eye: abbreviation })}
          </Button>
        </div>
      ) : (
        <LensFinder eye={eye} usage={pair.usage} onPick={setLens} />
      )}

      {eye === 'left' && right && lens?.variantId !== right.variantId ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => dispatch({ type: 'copyRightLensToLeft', pairId: pair.id })}
        >
          <Copy className="size-4" aria-hidden />
          {t('opticalOrders.lens.sameAsRight')}
        </Button>
      ) : null}

      {lensProblems.map((problem) => (
        <p key={problem.message.key} className="text-sm text-red-600" role="alert">
          <span dir="ltr" className="font-mono">
            {abbreviation}
          </span>{' '}
          {t(problem.message.key, problem.message.values)}
        </p>
      ))}
      {showErrors && !lens ? (
        <p className="text-sm text-red-600" role="alert">
          {t('opticalOrders.validation.chooseLens', { eye: abbreviation })}
        </p>
      ) : null}

      <FittingInput
        eye={eye}
        pair={pair}
        dispatch={dispatch}
        problems={problems}
        showErrors={showErrors}
      />
    </section>
  );
}

/** One lens for both eyes: a single search fills the right and left lines with the same lens, and each eye keeps its own fitting height. */
function SharedLensSection({
  pair,
  dispatch,
  problems,
  showErrors,
}: {
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const lens = pair.lenses.right;
  const lensProblems = problems.filter(
    (problem) => problem.field === 'lens.right' || problem.field === 'lens.left',
  );
  const setLens = (next: SelectedLens | null) =>
    dispatch({ type: 'setLens', pairId: pair.id, eye: 'right', lens: next });

  return (
    <section
      aria-labelledby={headingId}
      className="border-line space-y-4 rounded-xl border bg-white p-4"
    >
      <h3 id={headingId} className="text-ink flex items-center gap-2 text-sm font-semibold">
        <span dir="ltr" className="font-mono">
          OD + OS
        </span>
        <span aria-hidden>·</span>
        <span>{t('opticalOrders.lens.bothEyes')}</span>
      </h3>

      {lens ? (
        <div className="space-y-3">
          <SelectedLensSummary lens={lens} shared />
          <Button variant="secondary" size="sm" onClick={() => setLens(null)}>
            <RefreshCw className="size-4" aria-hidden />
            {t('opticalOrders.lens.changeBoth')}
          </Button>
        </div>
      ) : (
        <LensFinder eye="right" shared usage={pair.usage} onPick={setLens} />
      )}

      {lensProblems.map((problem) => (
        <p
          key={`${problem.field}-${problem.message.key}`}
          className="text-sm text-red-600"
          role="alert"
        >
          <span dir="ltr" className="font-mono">
            {problem.field === 'lens.right' ? 'OD' : 'OS'}
          </span>{' '}
          {t(problem.message.key, problem.message.values)}
        </p>
      ))}
      {showErrors && !lens ? (
        <p className="text-sm text-red-600" role="alert">
          {t('opticalOrders.validation.chooseLensBoth')}
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        {(['right', 'left'] as const).map((eye) => (
          <FittingInput
            key={eye}
            eye={eye}
            pair={pair}
            dispatch={dispatch}
            problems={problems}
            showErrors={showErrors}
          />
        ))}
      </div>
    </section>
  );
}

/** An eye's fitting height, checked as the server reads it; required when that eye's lens design needs one. */
function FittingInput({
  eye,
  pair,
  dispatch,
  problems,
  showErrors,
}: {
  eye: EyeSide;
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}) {
  const { t } = useTranslation();
  const lens = pair.lenses[eye];
  const abbreviation = EYE_ABBREVIATION[eye];
  const required = fittingRequired(lens);
  const fittingValue = pair.fitting[eye];
  const serverFitting = problems.find((problem) => problem.field === `fitting.${eye}`);
  const typedFitting = fittingProblem(fittingValue, required);
  const fittingError =
    serverFitting?.message ??
    (typedFitting && (showErrors || fittingValue.trim() !== '') ? typedFitting : null);

  return (
    <Input
      label={t('opticalOrders.fitting.label', { eye: abbreviation })}
      hint={
        lens
          ? required
            ? t('opticalOrders.fitting.required', {
                design: t(`catalog.lensType.${lens.details.lensType}`),
              })
            : t('opticalOrders.fitting.optional')
          : t('opticalOrders.fitting.chooseLensFirst')
      }
      required={required}
      dir="ltr"
      inputMode="decimal"
      autoComplete="off"
      placeholder="18.5"
      className="font-mono sm:max-w-40 rtl:text-right"
      value={fittingValue}
      error={fittingError ? t(fittingError.key, fittingError.values) : undefined}
      onChange={(event) =>
        dispatch({ type: 'setFitting', pairId: pair.id, eye, value: event.target.value })
      }
    />
  );
}

function coatingsOf(details: PrescriptionLensDetails): string[] {
  return [
    details.hasAntiReflective ? 'hasAntiReflective' : null,
    details.hasBlueLightFilter ? 'hasBlueLightFilter' : null,
    details.isPhotochromic ? 'isPhotochromic' : null,
    details.hasScratchResistant ? 'hasScratchResistant' : null,
  ].filter((value): value is string => value !== null);
}

/** One lens as the order will name it: design, material, index, coatings, how it is supplied, and its price and stock now. */
export function SelectedLensSummary({
  lens,
  shared = false,
}: {
  lens: SelectedLens;
  shared?: boolean;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const check = useCandidateCheckQuery([lens.variantId]);
  const line = check.data?.lines.find((entry) => entry.variantId === lens.variantId);
  const price = line?.unitPriceMinor ?? lens.seenPriceMinor;
  const stockProblem =
    shared && line ? sharedLensStockProblem(lens.details.supplyMode, line.availableQuantity) : null;

  return (
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-ink text-sm font-medium">
          {lens.productName}
          {lens.variantName ? ` — ${lens.variantName}` : ''}
        </p>
        <LensFacts details={lens.details} sku={lens.sku} />
        {line ? <Availability line={line} supplyMode={lens.details.supplyMode} /> : null}
        {stockProblem ? (
          <p className="text-sm text-red-700" role="alert">
            {t(stockProblem.key, stockProblem.values)}
          </p>
        ) : null}
      </div>
      <span className="text-ink text-sm font-semibold" dir="ltr">
        {price === null ? '—' : format(price)}
      </span>
    </div>
  );
}

function LensFacts({ details, sku }: { details: PrescriptionLensDetails; sku: string }) {
  const { t } = useTranslation();
  const coatings = coatingsOf(details);
  return (
    <div className="text-muted space-y-0.5 text-xs">
      <p>
        {t(`catalog.lensType.${details.lensType}`)} ·{' '}
        {t(`catalog.lensMaterial.${details.material}`)}
        {details.refractiveIndex !== null ? (
          <>
            {' · '}
            <span dir="ltr" className="font-mono">
              {details.refractiveIndex.toFixed(2)}
            </span>
          </>
        ) : null}
        {' · '}
        <span className="text-ink">{t(`opticalOrders.supply.${details.supplyMode}`)}</span>
      </p>
      {coatings.length > 0 ? (
        <p>{coatings.map((coating) => t(`catalog.fields.${coating}`)).join(' · ')}</p>
      ) : null}
      <p>
        <span className="font-mono" dir="ltr">
          {sku}
        </span>{' '}
        ·{' '}
        <span className="font-mono" dir="ltr">
          {lensRange(details)}
        </span>
      </p>
    </div>
  );
}

function LensFinder({
  eye,
  onPick,
  shared = false,
  usage,
}: {
  eye: EyeSide;
  onPick: (lens: SelectedLens) => void;
  shared?: boolean;
  usage: PairUsage | null;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const [term, setTerm] = useState('');
  const deferred = useDeferredValue(term.trim());
  const [productId, setProductId] = useState<string | null>(null);
  const products = useProductsQuery({
    type: ProductType.PRESCRIPTION_LENS,
    status: ProductStatus.ACTIVE,
    pageSize: 50,
    search: deferred,
  });
  const items = products.data?.items ?? [];

  if (productId) {
    return (
      <LensVariantList
        productId={productId}
        eye={eye}
        shared={shared}
        usage={usage}
        onPick={onPick}
        onBack={() => setProductId(null)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <Input
        type="search"
        label={
          shared
            ? t('opticalOrders.lens.searchBoth')
            : t('opticalOrders.lens.search', { eye: EYE_ABBREVIATION[eye] })
        }
        placeholder={t('opticalOrders.lens.searchPlaceholder')}
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      {usage === 'MULTIFOCAL' ? (
        <p className="text-muted text-xs">{t('opticalOrders.usage.multifocalGuide')}</p>
      ) : null}
      {products.isPending ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <p className="text-muted text-sm">{t('opticalOrders.lens.noProducts')}</p>
      ) : (
        <ul className="divide-line border-line divide-y rounded-lg border">
          {items.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                onClick={() => setProductId(product.id)}
                className="hover:bg-canvas flex w-full items-center gap-3 px-4 py-3 text-start"
              >
                <span className="min-w-0 flex-1">
                  <span
                    dir="auto"
                    className="text-ink block truncate text-sm font-medium rtl:text-right"
                  >
                    {product.name}
                  </span>
                  <span className="text-muted block text-xs">
                    {product.brandName ?? t('catalog.fields.noBrand')} ·{' '}
                    {t('opticalOrders.lens.variantCount', { count: product.variantCount })}
                  </span>
                </span>
                {product.fromPriceMinor !== null ? (
                  <span className="text-ink shrink-0 text-sm" dir="ltr">
                    {format(product.fromPriceMinor)}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface LensFilters {
  design: string;
  material: string;
  index: string;
  supply: string;
  coatings: string[];
}

const NO_FILTERS: LensFilters = { design: '', material: '', index: '', supply: '', coatings: [] };

function LensVariantList({
  productId,
  eye,
  shared,
  usage,
  onPick,
  onBack,
}: {
  productId: string;
  eye: EyeSide;
  shared: boolean;
  usage: PairUsage | null;
  onPick: (lens: SelectedLens) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const detail = useProductQuery(productId);
  const [filters, setFilters] = useState<LensFilters>(NO_FILTERS);
  // For multifocal glasses the list opens on multifocal designs only; one tick shows every design again.
  const [multifocalOnly, setMultifocalOnly] = useState(usage === 'MULTIFOCAL');

  const variants = useMemo(
    () =>
      (detail.data?.variants ?? []).filter(
        (
          variant,
        ): variant is ProductVariantSummary & { prescriptionLens: PrescriptionLensDetails } =>
          variant.isActive && variant.prescriptionLens !== null,
      ),
    [detail.data],
  );
  const check = useCandidateCheckQuery(variants.map((variant) => variant.id));

  const distinct = (read: (details: PrescriptionLensDetails) => string) =>
    [...new Set(variants.map((variant) => read(variant.prescriptionLens)))].filter(Boolean);
  const designs = distinct((details) => details.lensType);
  const materials = distinct((details) => details.material);
  const indices = distinct((details) =>
    details.refractiveIndex === null ? '' : details.refractiveIndex.toFixed(2),
  );
  const supplies = distinct((details) => details.supplyMode);
  const coatings = [
    ...new Set(variants.flatMap((variant) => coatingsOf(variant.prescriptionLens))),
  ];

  const shown = variants.filter(({ prescriptionLens: details }) => {
    if (multifocalOnly && !isMultifocalLensType(details.lensType)) return false;
    if (filters.design && details.lensType !== filters.design) return false;
    if (filters.material && details.material !== filters.material) return false;
    if (filters.index && details.refractiveIndex?.toFixed(2) !== filters.index) return false;
    if (filters.supply && details.supplyMode !== filters.supply) return false;
    return filters.coatings.every((coating) => coatingsOf(details).includes(coating));
  });

  if (detail.isPending) return <LoadingState />;
  const product = detail.data;
  if (!product) return null;

  const filterSelect = (
    key: 'design' | 'material' | 'index' | 'supply',
    label: string,
    values: string[],
    labelOf: (value: string) => string,
  ) =>
    values.length > 1 ? (
      <FormField label={label}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={filters[key]}
            options={[
              { value: '', label: t('common.all') },
              ...values.map((value) => ({ value, label: labelOf(value) })),
            ]}
            onChange={(event) => setFilters({ ...filters, [key]: event.target.value })}
          />
        )}
      </FormField>
    ) : null;

  return (
    <section className="space-y-3" aria-label={product.name}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p dir="auto" className="text-ink truncate text-sm font-semibold rtl:text-right">
            {product.name}
          </p>
          <p className="text-muted text-xs">{product.brandName ?? t('catalog.fields.noBrand')}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onBack}>
          {t('opticalOrders.lens.otherLenses')}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {filterSelect('design', t('catalog.fields.lensType'), designs, (value) =>
          t(`catalog.lensType.${value}`),
        )}
        {filterSelect('material', t('catalog.fields.material'), materials, (value) =>
          t(`catalog.lensMaterial.${value}`),
        )}
        {filterSelect('index', t('catalog.fields.refractiveIndex'), indices, (value) => value)}
        {filterSelect('supply', t('opticalOrders.lens.supply'), supplies, (value) =>
          t(`opticalOrders.supply.${value}`),
        )}
      </div>
      {usage === 'MULTIFOCAL' ? (
        <label className="text-ink flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={multifocalOnly}
            onChange={(event) => setMultifocalOnly(event.target.checked)}
          />
          {t('opticalOrders.usage.multifocalOnly')}
        </label>
      ) : null}
      {coatings.length > 1 ? (
        <fieldset className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
          <legend className="text-muted mb-1 text-xs">{t('opticalOrders.lens.coatings')}</legend>
          {coatings.map((coating) => (
            <label key={coating} className="text-ink flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.coatings.includes(coating)}
                onChange={(event) =>
                  setFilters({
                    ...filters,
                    coatings: event.target.checked
                      ? [...filters.coatings, coating]
                      : filters.coatings.filter((value) => value !== coating),
                  })
                }
              />
              {t(`catalog.fields.${coating}`)}
            </label>
          ))}
        </fieldset>
      ) : null}

      {shown.length === 0 ? (
        <p className="text-muted text-sm">{t('opticalOrders.lens.noMatch')}</p>
      ) : (
        <ul className="divide-line border-line divide-y rounded-lg border">
          {shown.map((variant) => {
            const details = variant.prescriptionLens;
            const line = check.data?.lines.find((entry) => entry.variantId === variant.id);
            const price = line?.unitPriceMinor ?? variant.sellingPriceMinor;
            const name = variant.variantName ?? product.name;
            // Both eyes take a piece each of a stocked lens: until the branch is known to hold two, the shared choice is not offered as valid.
            const stockProblem = shared
              ? line
                ? sharedLensStockProblem(details.supplyMode, line.availableQuantity)
                : details.supplyMode === 'STOCKED'
                  ? { key: 'opticalOrders.lens.sharedChecking' }
                  : null
              : null;
            return (
              <li
                key={variant.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-3"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-ink text-sm font-medium">{name}</p>
                  <LensFacts details={details} sku={variant.sku} />
                  {isMultifocalLensType(details.lensType) ? (
                    <p className="text-muted text-xs">{t('opticalOrders.lens.multifocalHint')}</p>
                  ) : null}
                  {line ? <Availability line={line} supplyMode={details.supplyMode} /> : null}
                  {stockProblem ? (
                    <p className="text-xs text-red-700">{t(stockProblem.key)}</p>
                  ) : null}
                </div>
                {/* On a phone the price and the button take their own line, so the lens's facts keep the full width. */}
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="text-ink text-sm font-medium" dir="ltr">
                    {format(price)}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={stockProblem !== null}
                    aria-label={
                      shared
                        ? t('opticalOrders.lens.chooseForBoth', { name })
                        : t('opticalOrders.lens.chooseFor', { name, eye: EYE_ABBREVIATION[eye] })
                    }
                    onClick={() =>
                      onPick({
                        productId: product.id,
                        variantId: variant.id,
                        productName: product.name,
                        variantName: variant.variantName,
                        sku: variant.sku,
                        brandName: product.brandName,
                        details,
                        seenPriceMinor: line?.unitPriceMinor ?? variant.sellingPriceMinor,
                      })
                    }
                  >
                    {t('opticalOrders.lens.choose')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
